// sections/inspiration-gallery.liquid – the carousel element of sections/product-gallery.liquid 1:1 (copied from the live
// theme 02.11: arrows, mouse drag, autoplay; change both together) + Shop the look dots
if (!customElements.get('product-gallery-carousel')) {
  customElements.define('product-gallery-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.nav = this.querySelector('.pg-carousel__nav');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.prev.addEventListener('click', () => this.go(-1));
      this.next.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      new ResizeObserver(() => this.update()).observe(this.track);
      this.dragWithMouse();
      this.update();
      this.autoplay();
    }

    // Autoplay (owner 27.10): every few seconds the row glides on by one photo – slowly, easing in and out –
    // and from the end glides back to the start. Waits while the mouse is over the photos, a finger holds them,
    // the row is off screen or the tab hidden; a finger, drag, wheel or arrow takes over at once and the next
    // glide comes a full interval later. Off for "reduce motion".
    autoplay() {
      this.interval = Number(this.dataset.autoplay) * 1000;
      if (!this.interval || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.played = new Set();
      this.started = new Set();
      const t = this.track;
      const user = () => { this.stopGlide(); this.schedule(); };
      t.addEventListener('pointerdown', user);
      t.addEventListener('keydown', user);
      t.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) user(); }, { passive: true });
      t.addEventListener('touchstart', () => { this.held = true; user(); }, { passive: true });
      const release = () => { this.held = false; this.schedule(); };
      t.addEventListener('touchend', release);
      t.addEventListener('touchcancel', release);
      t.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { this.hover = true; this.schedule(); } });
      t.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { this.hover = false; this.schedule(); } });
      this.nav.addEventListener('click', user);
      new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; this.schedule(); }, { threshold: 0.5 }).observe(t);
      document.addEventListener('visibilitychange', () => this.schedule());
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.unwait) this.unwait();
      if (!this.visible || this.hover || this.held || document.hidden) return;
      const video = this.videoInView();
      if (video) return this.waitForVideo(video);
      this.timer = setTimeout(() => this.step(), this.interval);
    }

    // Video (owner 27.10: "don't move on until the video ends"): a video fully in view plays from its start and
    // the row waits until it ends (it loops, so the end = the moment it starts over), then glides on at once.
    // It waits for it again only after the video has left the view and come back.
    videoInView() {
      const t = this.track;
      const left = t.scrollLeft;
      const right = left + t.clientWidth;
      let found = null;
      for (const slide of t.children) {
        const video = slide.querySelector('video');
        if (!video) continue;
        if (slide.offsetLeft >= left - 2 && slide.offsetLeft + slide.offsetWidth <= right + 2) {
          if (!found && !this.played.has(video)) found = video;
        } else {
          this.played.delete(video);
          this.started.delete(video);
        }
      }
      return found;
    }

    waitForVideo(video) {
      if (!this.started.has(video)) {
        this.started.add(video);
        video.currentTime = 0;
        video.play().catch(() => {});
      }
      let last = video.currentTime;
      const tick = () => {
        if (video.currentTime < last - 0.5) return done();
        last = video.currentTime;
      };
      const done = () => {
        this.unwait();
        this.played.add(video);
        this.step();
      };
      video.addEventListener('timeupdate', tick);
      video.addEventListener('ended', done);
      // a video that cannot play (e.g. iPhone power saving) does not hold the row for good
      const rest = Number.isFinite(video.duration) ? video.duration - video.currentTime : 15;
      this.timer = setTimeout(done, (rest + 1) * 1000);
      this.unwait = () => {
        clearTimeout(this.timer);
        video.removeEventListener('timeupdate', tick);
        video.removeEventListener('ended', done);
        this.unwait = null;
      };
    }

    step() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      if (max < 2) return;
      const left = t.scrollLeft;
      if (left >= max - 2) return this.glide(0, 2600);
      const slide = [...t.children].find((el) => el.offsetLeft > left + 2);
      this.glide(slide ? Math.min(slide.offsetLeft, max) : max, 1600);
    }

    glide(target, duration) {
      const t = this.track;
      const from = t.scrollLeft;
      const start = performance.now();
      t.classList.add('is-gliding');
      const frame = (now) => {
        const p = Math.min((now - start) / duration, 1);
        // sine ease in-out: starts and stops softly, no jolt at either end
        t.scrollLeft = from + ((target - from) * (1 - Math.cos(Math.PI * p))) / 2;
        if (p < 1) {
          this.glideFrame = requestAnimationFrame(frame);
        } else {
          this.stopGlide();
          this.schedule();
        }
      };
      this.glideFrame = requestAnimationFrame(frame);
    }

    stopGlide() {
      cancelAnimationFrame(this.glideFrame);
      this.track.classList.remove('is-gliding');
    }

    // Mouse: hold a photo and move sideways to scroll the row (finger and trackpad scroll it natively).
    // On release the row glides on a little and stops at the start of the nearest photo; a drag is not a click,
    // so captions (fabric, @author) open only on a plain click.
    dragWithMouse() {
      const t = this.track;
      let start = null;
      let dragged = false;
      let moves = [];
      t.addEventListener('dragstart', (e) => e.preventDefault());
      t.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        start = { x: e.clientX, left: t.scrollLeft, id: e.pointerId };
        dragged = false;
        moves = [{ x: e.clientX, time: e.timeStamp }];
      });
      t.addEventListener('pointermove', (e) => {
        if (!start || e.pointerId !== start.id) return;
        const dx = e.clientX - start.x;
        if (!dragged) {
          if (Math.abs(dx) < 5) return;
          dragged = true;
          t.classList.add('is-dragging');
          t.setPointerCapture(e.pointerId);
        }
        t.scrollLeft = start.left - dx;
        moves.push({ x: e.clientX, time: e.timeStamp });
        if (moves.length > 6) moves.shift();
      });
      const end = (e) => {
        if (!start || e.pointerId !== start.id) return;
        start = null;
        if (!dragged) return;
        // speed of the last moves (px per ms) → a short glide in the same direction
        const a = moves[0];
        const b = moves[moves.length - 1];
        const speed = b && a && b.time > a.time && e.timeStamp - b.time < 100 ? (b.x - a.x) / (b.time - a.time) : 0;
        const max = t.scrollWidth - t.clientWidth;
        const aim = Math.min(Math.max(t.scrollLeft - speed * 250, 0), max);
        let target = max;
        let best = Infinity;
        for (const slide of t.children) {
          const left = Math.min(slide.offsetLeft, max);
          if (Math.abs(left - aim) < best) { best = Math.abs(left - aim); target = left; }
        }
        if (Math.abs(max - aim) < best) target = max;
        t.scrollTo({ left: target, behavior: 'smooth' });
        clearTimeout(this.dragTimer);
        this.dragTimer = setTimeout(() => t.classList.remove('is-dragging'), 500);
      };
      t.addEventListener('pointerup', end);
      t.addEventListener('pointercancel', end);
      // the click that ends a drag does not open a link or the fabric drawer
      t.addEventListener('click', (e) => {
        if (dragged) { e.preventDefault(); e.stopPropagation(); dragged = false; }
      }, true);
    }

    // Page starts: one page = the visible width, each start moved back to the left edge of the photo
    // cut at the right, so after an arrow click no photo is cut at the start.
    stops() {
      const t = this.track;
      const view = t.clientWidth;
      const max = t.scrollWidth - view;
      const slides = [...t.children];
      const stops = [0];
      while (stops[stops.length - 1] < max - 2 && stops.length <= slides.length) {
        const left = stops[stops.length - 1];
        // a photo wider than the view (landscape on a phone) counts as seen – the next page starts after it
        const cut = slides.find((el) => el.offsetLeft > left + 1 && el.offsetLeft + el.offsetWidth > left + view + 1);
        stops.push(cut ? Math.min(cut.offsetLeft, max) : max);
      }
      return stops;
    }

    page(stops) {
      const left = this.track.scrollLeft;
      let i = 0;
      while (i + 1 < stops.length && stops[i + 1] <= left + 2) i++;
      return i;
    }

    go(dir) {
      const stops = this.stops();
      const i = this.page(stops);
      const left = this.track.scrollLeft;
      // back from between two starts = to the start of the current page
      const back = left > stops[i] + 2 ? i : Math.max(i - 1, 0);
      const target = dir > 0 ? stops[Math.min(i + 1, stops.length - 1)] : stops[back];
      this.track.scrollTo({ left: target, behavior: 'smooth' });
    }

    update() {
      const stops = this.stops();
      const i = this.page(stops);
      this.nav.hidden = stops.length < 2;
      this.prev.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = i === stops.length - 1;
    }
  });
}

// Shop the look: a tap / click on a dot opens its card (touch screens have no hover);
// a second tap, a tap elsewhere or scrolling the carousel closes it
(function () {
  if (window.inspirationGalleryReady) return;
  window.inspirationGalleryReady = true;

  // autoplay waits while a card is open (otherwise the glide to the next photo would close it)
  var Carousel = customElements.get('product-gallery-carousel');
  if (Carousel && Carousel.prototype.schedule && !Carousel.prototype.stlPatched) {
    var schedule = Carousel.prototype.schedule;
    Carousel.prototype.schedule = function () {
      if (this.querySelector('.stl-spot.is-open')) {
        clearTimeout(this.timer);
        return;
      }
      return schedule.apply(this, arguments);
    };
    Carousel.prototype.stlPatched = true;
  }
  function pause(spot) {
    var carousel = spot.closest('product-gallery-carousel');
    if (carousel && carousel.stopGlide) {
      carousel.stopGlide();
      clearTimeout(carousel.timer);
    }
  }
  function resume(spot) {
    var carousel = spot.closest('product-gallery-carousel');
    if (carousel && carousel.schedule && carousel.interval) carousel.schedule();
  }

  function closeAll(root, except) {
    (root || document).querySelectorAll('.stl-spot.is-open').forEach(function (spot) {
      if (spot === except) return;
      spot.classList.remove('is-open');
      spot.querySelector('[data-stl-dot]').setAttribute('aria-expanded', 'false');
      resume(spot);
    });
  }

  // a photo wider than the screen (landscape on a phone): slide the carousel so the opened card is fully visible
  var ignoreScrollUntil = 0;
  function keepInView(spot) {
    var card = spot.querySelector('.stl-card');
    var track = spot.closest('[data-track]');
    if (!card || !track) return;
    var r = card.getBoundingClientRect();
    var t = track.getBoundingClientRect();
    var left = Math.max(t.left, 0) + 8;
    var right = Math.min(t.right, window.innerWidth) - 8;
    var shift = r.left < left ? r.left - left : r.right > right ? r.right - right : 0;
    if (!shift) return;
    ignoreScrollUntil = Date.now() + 800;
    track.style.scrollSnapType = 'none';
    track.scrollBy({ left: shift, behavior: 'smooth' });
    setTimeout(function () { track.style.scrollSnapType = ''; }, 800);
  }

  document.addEventListener('click', function (e) {
    var dot = e.target.closest && e.target.closest('[data-stl-dot]');
    if (dot) {
      var spot = dot.closest('.stl-spot');
      var open = !spot.classList.contains('is-open');
      closeAll(null, spot);
      spot.classList.toggle('is-open', open);
      dot.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        pause(spot);
        keepInView(spot);
      } else {
        resume(spot);
      }
      return;
    }
    if (!(e.target.closest && e.target.closest('.stl-card'))) closeAll();
  });

  document.addEventListener('scroll', function (e) {
    if (Date.now() < ignoreScrollUntil) return;
    if (e.target.matches && e.target.matches('.section--inspiration-gallery [data-track]')) closeAll(e.target);
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll();
  });
})();